import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Shield, Eye, Database, UserX } from 'lucide-react';
import { Card } from '../components/ui/card';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-8">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-white p-2">
            <ChevronLeft className="w-6 h-6" />
          </button>
          <Eye className="w-6 h-6 text-pink-300" />
          <h1 className="text-xl font-bold text-white">Politique de Confidentialité</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        <Card className="p-6 bg-white/10 backdrop-blur-md border-white/20">
          <div className="prose prose-invert max-w-none">
            <p className="text-pink-200 text-sm mb-4">Dernière mise à jour : Mars 2026</p>

            <div className="p-4 bg-purple-500/20 rounded-lg border border-purple-400/30 mb-6">
              <p className="text-white text-sm">
                <strong>Notre engagement :</strong> Votre vie privée est notre priorité. Vos données de santé sont 
                strictement confidentielles et ne seront jamais vendues à des tiers.
              </p>
            </div>

            <h2 className="text-xl font-bold text-white mt-6 mb-3">1. Données Collectées</h2>
            <p className="text-pink-100 text-sm leading-relaxed mb-3">
              Nous collectons les informations suivantes :
            </p>
            
            <h3 className="text-lg font-semibold text-pink-300 mt-4 mb-2">Données d'inscription</h3>
            <ul className="list-disc list-inside text-pink-100 text-sm mb-4 space-y-1">
              <li>Prénom et nom</li>
              <li>Adresse email ou numéro de téléphone</li>
              <li>Mot de passe (chiffré)</li>
              <li>Établissement scolaire (optionnel)</li>
            </ul>

            <h3 className="text-lg font-semibold text-pink-300 mt-4 mb-2">Données de santé</h3>
            <ul className="list-disc list-inside text-pink-100 text-sm mb-4 space-y-1">
              <li>Informations sur le cycle menstruel</li>
              <li>Données de contraception</li>
              <li>Symptômes enregistrés</li>
              <li>Historique des rappels</li>
            </ul>

            <h3 className="text-lg font-semibold text-pink-300 mt-4 mb-2">Données d'utilisation</h3>
            <ul className="list-disc list-inside text-pink-100 text-sm mb-4 space-y-1">
              <li>Progression dans les modules éducatifs</li>
              <li>Résultats des quiz</li>
              <li>Points et badges obtenus</li>
            </ul>

            <h2 className="text-xl font-bold text-white mt-6 mb-3">2. Utilisation des Données</h2>
            <p className="text-pink-100 text-sm leading-relaxed mb-4">
              Vos données sont utilisées pour :
            </p>
            <ul className="list-disc list-inside text-pink-100 text-sm mb-4 space-y-1">
              <li>Personnaliser votre expérience dans l'application</li>
              <li>Calculer les prédictions de votre cycle</li>
              <li>Envoyer des rappels personnalisés</li>
              <li>Améliorer nos contenus éducatifs</li>
              <li>Générer des statistiques anonymisées (jamais individuelles)</li>
            </ul>

            <h2 className="text-xl font-bold text-white mt-6 mb-3">3. Protection des Données</h2>
            <p className="text-pink-100 text-sm leading-relaxed mb-4">
              Nous mettons en œuvre des mesures de sécurité robustes :
            </p>
            <ul className="list-disc list-inside text-pink-100 text-sm mb-4 space-y-1">
              <li>Chiffrement des données en transit (HTTPS/TLS)</li>
              <li>Chiffrement des mots de passe (bcrypt)</li>
              <li>Accès restreint aux données de santé</li>
              <li>Serveurs sécurisés avec authentification</li>
            </ul>

            <h2 className="text-xl font-bold text-white mt-6 mb-3">4. Partage des Données</h2>
            <p className="text-pink-100 text-sm leading-relaxed mb-4">
              <strong className="text-pink-300">Nous ne vendons jamais vos données.</strong><br />
              Vos données peuvent être partagées uniquement dans les cas suivants :
            </p>
            <ul className="list-disc list-inside text-pink-100 text-sm mb-4 space-y-1">
              <li>Avec votre consentement explicite</li>
              <li>Pour répondre à des obligations légales</li>
              <li>Sous forme anonymisée pour des partenaires (ONG, ministères) à des fins de santé publique</li>
            </ul>

            <h2 className="text-xl font-bold text-white mt-6 mb-3">5. Vos Droits</h2>
            <p className="text-pink-100 text-sm leading-relaxed mb-4">
              Conformément aux réglementations en vigueur, vous avez le droit de :
            </p>
            <ul className="list-disc list-inside text-pink-100 text-sm mb-4 space-y-1">
              <li><strong>Accéder</strong> à vos données personnelles</li>
              <li><strong>Rectifier</strong> vos informations</li>
              <li><strong>Supprimer</strong> votre compte et vos données</li>
              <li><strong>Exporter</strong> vos données dans un format lisible</li>
              <li><strong>Retirer</strong> votre consentement à tout moment</li>
            </ul>

            <h2 className="text-xl font-bold text-white mt-6 mb-3">6. Conservation des Données</h2>
            <p className="text-pink-100 text-sm leading-relaxed mb-4">
              Vos données sont conservées tant que votre compte est actif. En cas de suppression de compte, 
              vos données sont effacées dans un délai de 30 jours, sauf obligation légale de conservation.
            </p>

            <h2 className="text-xl font-bold text-white mt-6 mb-3">7. Cookies et Technologies</h2>
            <p className="text-pink-100 text-sm leading-relaxed mb-4">
              L'application utilise des technologies de stockage local pour :
            </p>
            <ul className="list-disc list-inside text-pink-100 text-sm mb-4 space-y-1">
              <li>Maintenir votre session de connexion</li>
              <li>Mémoriser vos préférences</li>
              <li>Améliorer les performances de l'application</li>
            </ul>

            <h2 className="text-xl font-bold text-white mt-6 mb-3">8. Mineurs</h2>
            <p className="text-pink-100 text-sm leading-relaxed mb-4">
              Fleur de Vie peut être utilisée par des mineures. Les données des utilisatrices de moins de 
              16 ans bénéficient d'une protection renforcée et ne sont jamais partagées avec des tiers.
            </p>

            <h2 className="text-xl font-bold text-white mt-6 mb-3">9. Contact DPO</h2>
            <p className="text-pink-100 text-sm leading-relaxed mb-4">
              Pour toute question relative à vos données personnelles, contactez notre Délégué à la Protection des Données :<br />
              <span className="text-pink-300">dpo@fleurde-vie.com</span>
            </p>

            <div className="mt-8 p-4 bg-green-500/20 rounded-lg border border-green-400/30">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-white font-semibold">Votre vie privée est protégée</span>
              </div>
              <p className="text-green-100 text-sm">
                Nous nous engageons à respecter les standards les plus élevés en matière de protection des données.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
